import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { BdrLead, BdrStats } from '@/types'
import { parseBdrPaginatedResponse } from '@/utils/bdrApi'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { emptyStats } from '@/utils/bdrPanelUtils'
import { isRequestCanceled } from '@/utils/isRequestCanceled'

export type BdrPanelFetchQueryDeps = {
  currentBdrId: string
  search: string
  filterStatus: string
  filterTemp: string
  filterFase: string
  sortField: string
  sortDir: string
  nurturingSearch: string
  bankSearch: string
  bankFilterSegmento: string
  bankFilterPlataforma: string
  bankFilterOrigem: string
  bankSortField: string
  bankSortDir: string
  setLeadsList: Dispatch<SetStateAction<BdrLead[]>>
  setQueueLeads: Dispatch<SetStateAction<BdrLead[]>>
  setNurturingLeads: Dispatch<SetStateAction<BdrLead[]>>
  setHistoryAgendamentos: Dispatch<
    SetStateAction<
      {
        id: string
        empresa: string
        contato: string
        dataEntrada?: string
        dataAgendada?: string
        score: number
        status: string
        reuniaoStatus?: string
      }[]
    >
  >
  setStats: Dispatch<SetStateAction<BdrStats>>
  setIsFiltering: (v: boolean) => void
  setLoadingMore: (v: boolean) => void
  setLoadingNurturing: (v: boolean) => void
  setLoadingHistory: (v: boolean) => void
  setLoadingBank: (v: boolean) => void
  setPage: (p: number) => void
  setHasMore: (v: boolean) => void
  setTotalLeads: (n: number) => void
  setNurturingPage: (p: number) => void
  setNurturingHasMore: (v: boolean) => void
  setNurturingTotal: (n: number) => void
  setBankLeads: Dispatch<SetStateAction<BdrLead[]>>
  setBankPage: (p: number) => void
  setBankTotalPages: (n: number) => void
}

export function useBdrPanelFetchQueries(deps: BdrPanelFetchQueryDeps) {
  const abortRef = useRef<AbortController | null>(null)

  const fetchAllLeads = useCallback(
    async (p = 1, append = false) => {
      if (append) deps.setLoadingMore(true)
      else deps.setIsFiltering(true)

      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      try {
        const params: Record<string, string | number> = {
          bdrId: deps.currentBdrId,
          page: p,
          limit: 30,
          all: 'true',
        }
        if (deps.search) params.search = deps.search
        if (deps.filterStatus) params.status = deps.filterStatus
        if (deps.filterTemp) params.temp = deps.filterTemp
        if (deps.filterFase) params.fase = deps.filterFase
        if (deps.sortField) params.sortField = deps.sortField
        if (deps.sortDir) params.sortDir = deps.sortDir

        const { data } = await api.get('/bdr/queue', { params, signal: controller.signal })
        const { items, total, hasMore: more } = parseBdrPaginatedResponse(data)

        deps.setLeadsList((prev) => (append ? [...prev, ...items] : items))
        deps.setTotalLeads(total)
        deps.setHasMore(more)
        deps.setPage(p)
      } catch (err: unknown) {
        if (!isRequestCanceled(err)) {
          toast.error(getFriendlyError(err, 'Erro ao carregar leads'))
        }
      } finally {
        deps.setIsFiltering(false)
        deps.setLoadingMore(false)
      }
    },
    [
      deps.currentBdrId,
      deps.search,
      deps.filterStatus,
      deps.filterTemp,
      deps.filterFase,
      deps.sortField,
      deps.sortDir,
    ],
  )

  const fetchTodayQueue = useCallback(async () => {
    try {
      const dashRes = await api.get('/bdr/dashboard', { params: { bdrId: deps.currentBdrId } })
      deps.setStats(dashRes.data?.data ?? dashRes.data ?? emptyStats())

      const pageSize = 100
      const maxPages = 10
      let pageNum = 1
      let combined: BdrLead[] = []
      let hasMore = true

      while (hasMore && pageNum <= maxPages) {
        const { data } = await api.get('/bdr/queue', {
          params: { bdrId: deps.currentBdrId, page: pageNum, limit: pageSize },
        })
        const { items, hasMore: more } = parseBdrPaginatedResponse(data)
        combined = pageNum === 1 ? items : [...combined, ...items]
        hasMore = more
        pageNum += 1
      }

      deps.setQueueLeads(combined)
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao carregar fila do dia'))
    }
  }, [deps.currentBdrId])

  const fetchNurturingLeads = useCallback(
    async (p = 1, append = false) => {
      if (append) deps.setLoadingMore(true)
      else deps.setLoadingNurturing(true)

      try {
        const params: Record<string, string | number> = {
          bdrId: deps.currentBdrId,
          page: p,
          limit: 30,
        }
        if (deps.nurturingSearch) params.search = deps.nurturingSearch

        const { data } = await api.get('/bdr/nurturing', { params })
        const { items, total, hasMore: more } = parseBdrPaginatedResponse(data)

        deps.setNurturingLeads((prev) => (append ? [...prev, ...items] : items))
        deps.setNurturingTotal(total)
        deps.setNurturingHasMore(more)
        deps.setNurturingPage(p)
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao carregar leads em nutrição'))
      } finally {
        deps.setLoadingNurturing(false)
        deps.setLoadingMore(false)
      }
    },
    [deps.currentBdrId, deps.nurturingSearch],
  )

  const fetchAppointmentHistory = useCallback(async () => {
    deps.setLoadingHistory(true)
    try {
      const { data } = await api.get('/bdr/appointment-history', {
        params: { bdrId: deps.currentBdrId },
      })
      const months = Array.isArray(data) ? data : []
      const leads = months.flatMap((month: { leads?: Record<string, unknown>[] }) =>
        (month.leads ?? []).map((row) => ({
          id: (row.actualId as string) || (row.id as string),
          empresa: (row.empresa as string) || (row.name as string) || '',
          contato: (row.contato as string) || (row.name as string) || '',
          dataEntrada: row.dataAgendada as string | undefined,
          dataAgendada: row.dataAgendada as string | undefined,
          score: (row.score as number) ?? 0,
          status: 'reuniao_realizada',
          reuniaoStatus: row.reuniaoStatus as string | undefined,
        })),
      )
      deps.setHistoryAgendamentos(leads)
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao carregar histórico de reuniões realizadas'))
    } finally {
      deps.setLoadingHistory(false)
    }
  }, [deps.currentBdrId])

  const fetchBankLeads = useCallback(
    async (p = 1) => {
      deps.setLoadingBank(true)
      try {
        const params: Record<string, string | number> = {
          page: p,
          pageSize: 10,
          search: deps.bankSearch,
          sortField: deps.bankSortField,
          sortDir: deps.bankSortDir,
        }
        if (deps.bankFilterSegmento) params.segmento = deps.bankFilterSegmento
        if (deps.bankFilterPlataforma) params.plataforma = deps.bankFilterPlataforma
        if (deps.bankFilterOrigem) params.origem = deps.bankFilterOrigem

        const resp = await api.get('/bdr/migrated-lost', { params })
        deps.setBankLeads(resp.data.data ?? [])
        deps.setBankTotalPages(resp.data.totalPages ?? 1)
        deps.setBankPage(p)
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao carregar leads do banco'))
      } finally {
        deps.setLoadingBank(false)
      }
    },
    [
      deps.bankSearch,
      deps.bankFilterSegmento,
      deps.bankFilterPlataforma,
      deps.bankFilterOrigem,
      deps.bankSortField,
      deps.bankSortDir,
    ],
  )

  return {
    fetchAllLeads,
    fetchTodayQueue,
    fetchNurturingLeads,
    fetchAppointmentHistory,
    fetchBankLeads,
  }
}
